/**
 * useArtifactVersions — fetches and exposes version history for a Nebula artifact.
 *
 * Usage:
 *   const { versions, pinnedVersion, isLoading, loadVersion, versionContent,
 *           isLoadingVersion, clearVersionContent, pinVersion }
 *     = useArtifactVersions(answerId, chatOwnerId);
 *
 * - `versions`        — [{ version_number, saved_at, trigger }] sorted ascending (no content)
 * - `pinnedVersion`   — version_number of the pinned version, or null
 * - `pinVersion(n)`   — pin version n (toggle: same number → unpin, null → unpin)
 * - `loadVersion(n)`  — fetches full content for version n into `versionContent`
 * - `versionContent`  — { version_number, saved_at, trigger, content } | null
 * - `clearVersionContent` — resets preview back to null (user dismissed version panel)
 */

import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../api";
import ENDPOINTS from "../api/endpoints";
import QUERYKEYS from "../api/queryKeys";
import { useSparkContext } from "../context/SparkContext";
import logger from "../services/Logger";

export default function useArtifactVersions(answerId, chatOwnerId) {
  const { chat_id, space_id } = useSparkContext();
  const queryClient = useQueryClient();

  const [versionContent, setVersionContent] = useState(null);
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: [QUERYKEYS.ARTIFACT_VERSIONS, chat_id, answerId],
    queryFn: async () => {
      const res = await api({
        method: "GET",
        url: ENDPOINTS.ANSWER_VERSIONS(
          chat_id, answerId, chatOwnerId, space_id
        ),
      });
      return {
        versions: res.data?.versions ?? [],
        pinned_version: res.data?.pinned_version ?? null,
      };
    },
    enabled: !!answerId && !!chat_id && !!chatOwnerId,
    staleTime: 30_000,
  });

  const pinMutation = useMutation({
    mutationFn: async ({ versionNumber, userId }) => {
      const res = await api({
        method: "POST",
        url: ENDPOINTS.PIN_ANSWER_VERSION,
        data: {
          user_id: userId,
          owner_id: userId,
          space_id,
          chat_id,
          answer_id: answerId,
          version_number: versionNumber,
        },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERYKEYS.ARTIFACT_VERSIONS, chat_id, answerId],
      });
    },
    onError: (err, variables) => {
      // Previously unhandled — a failed pin/unpin silently did nothing, with
      // no rollback needed (no optimistic update here) but also no visibility.
      logger.error("useArtifactVersions", "Failed to pin/unpin version", {
        err: err?.message,
        chat_id,
        answer_id: answerId,
        version_number: variables?.versionNumber,
      });
    },
  });

  const pinVersion = useCallback(
    (versionNumber, userId) => {
      // Toggle: if already pinned, unpin
      const currentPinned = data?.pinned_version ?? null;
      const next = currentPinned === versionNumber ? null : versionNumber;
      pinMutation.mutate({ versionNumber: next, userId });
    },
    [data?.pinned_version, pinMutation]
  );

  const loadVersion = useCallback(
    async (versionNumber) => {
      if (!answerId || !chat_id) return;
      setIsLoadingVersion(true);
      try {
        const res = await api({
          method: "GET",
          url: ENDPOINTS.ANSWER_VERSION(
            chat_id, answerId, versionNumber, chatOwnerId, space_id
          ),
        });
        setVersionContent(res.data?.version ?? null);
      } catch (err) {
        // Previously unhandled — both call sites in NebulaCanvas.jsx invoke this
        // fire-and-forget (no .catch()), so a failure was an unhandled promise
        // rejection. isLoadingVersion still clears via finally below, so the
        // spinner stops, but versionContent never updates — browsing/restoring
        // a version silently does nothing on failure. At minimum, log it.
        logger.error("useArtifactVersions", "Failed to load version content", {
          err: err?.message,
          chat_id,
          answer_id: answerId,
          version_number: versionNumber,
        });
      } finally {
        setIsLoadingVersion(false);
      }
    },
    [answerId, chatOwnerId, chat_id, space_id]
  );

  const clearVersionContent = useCallback(() => setVersionContent(null), []);

  return {
    versions: data?.versions ?? [],
    pinnedVersion: data?.pinned_version ?? null,
    isLoading,
    loadVersion,
    versionContent,
    isLoadingVersion,
    clearVersionContent,
    pinVersion,
  };
}
